import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Select } from 'ts/interfaces/Generic';
import { faHotel, faLocationDot } from '@fortawesome/free-solid-svg-icons';
import SelectCustom from '../SelectCustom/SelectCustom';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import withErrorBoundary from 'components/ui/withErrorBoundaries';
import { getGooglePrediction, getGooglePredictionDetails } from 'utils/ui';

interface AutocompleteGoogleProps {
  onSelect: (value: {
    lat: number;
    lng: number;
    city: string;
    id: string;
    types: string;
    locationName: string;
    fullValue: any;
    placeId: string;
  }) => void;
  value?: string;
  isSubmitted?: boolean;
  movablePlaceholderIcon?: any;
  movablePlaceholder?: string;
  sessionStorageKey?: string;
  defaultCity?: string;
}

const AutocompleteGoogle: FC<AutocompleteGoogleProps> = (props) => {
  const [inputText, setInputText] = useState('');
  const [suggestions, setSuggestions] = useState<Select[]>([]);
  const [isRtl, setIsRtl] = useState(false);
  const [urlParams] = useSearchParams();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const prevSuggestions = useRef<Select[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    (async () => {
      if (props.defaultCity) {
        const defaultCity = props.defaultCity;
        const predictions = await getGooglePrediction(
          { query: defaultCity, prefix: 'htl' },
          new AbortController().signal
        );

        if (predictions && predictions.length > 0 && props.defaultCity) {
          const placeId = predictions[0].place_id;

          const { result, status }: any = await getGooglePredictionDetails(placeId);

          if (status === google.maps.places.PlacesServiceStatus.OK && result) {
            const countryIndex =
              result.address_components?.findIndex((item: any) => item.types[0] === 'country') ??
              -1;
            const clearresult = {
              address_components: result.address_components,
              formatted_address: result.formatted_address,
              formatted_phone_number: result.formatted_phone_number,
              geometry: result.geometry,
              international_phone_number: result.international_phone_number,
              name: result.name,
              types: result.types,
              website: result.website,
            };
            if (props.sessionStorageKey) {
              sessionStorage.setItem(props.sessionStorageKey, JSON.stringify(clearresult));
            }

            props.onSelect({
              lat: result.geometry?.location?.lat ?? 0,
              lng: result.geometry?.location?.lng ?? 0,
              city: defaultCity,
              id: countryIndex >= 0 ? result.address_components![countryIndex].short_name : '',
              types: JSON.stringify(result.types ?? []),
              locationName: result.name ?? '',
              fullValue: clearresult,
              placeId: placeId,
            });

            const newSuggestions = [
              {
                label: defaultCity,
                value: placeId,
                html: (
                  <div>
                    <FontAwesomeIcon
                      icon={faLocationDot}
                      className="mr-10"
                      size="sm"
                      color="var(--color-300)"
                    />
                    <span
                      style={{ maxWidth: 'calc(100% - 20px)' }}
                      className="text-limited display-ib"
                    >
                      {defaultCity}
                    </span>
                  </div>
                ),
              },
            ];

            setSuggestions(newSuggestions);
            prevSuggestions.current = newSuggestions;
          }
        }
      } else if (urlParams.get('city')) {
        const defaultCity = urlParams.get('city');

        const newSuggestions = [
          {
            label: defaultCity ?? '',
            value: props.value ?? 'default',
            html: (
              <div>
                <FontAwesomeIcon
                  icon={faLocationDot}
                  className="mr-10"
                  size="sm"
                  color="var(--color-300)"
                />
                <span style={{ maxWidth: 'calc(100% - 20px)' }} className="text-limited display-ib">
                  {defaultCity}
                </span>
              </div>
            ),
          },
        ];

        setSuggestions(newSuggestions);
        prevSuggestions.current = newSuggestions;
      } else if (props.value === '') {
      }
    })();
  }, [urlParams]); //eslint-disable-line

  const performSearch = useCallback(
    async (val: string) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();

      const predictions = await getGooglePrediction(
        { query: val, prefix: 'htl' },
        abortControllerRef.current.signal
      );

      if (!predictions) {
        setSuggestions([]);
        return;
      }

      const filtered = predictions.filter((p: any) => !p.types.includes('country'));

      setIsMenuOpen(!!filtered.length);
      const newSuggestions = filtered.map((p) => ({
        value: p.place_id,
        label: p.description,
        html: (
          <div>
            {p.types.includes('lodging') ? (
              <FontAwesomeIcon
                icon={faHotel}
                className="mr-10"
                size="sm"
                color="var(--color-300)"
              />
            ) : (
              <FontAwesomeIcon
                icon={faLocationDot}
                className="mr-10"
                size="sm"
                color="var(--color-300)"
              />
            )}

            <span
              style={{ maxWidth: 'calc(100% - 20px)' }}
              className="text-limited display-ib"
              dangerouslySetInnerHTML={{
                __html: val.split(' ').reduce((text: string, word: string) => {
                  if (!word) return text;
                  const escapedWord = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                  return text.replace(
                    new RegExp(`(?<!<[^>]*)${escapedWord}(?![^<]*>)`, 'gi'),
                    (match: string) => `<span class="text-bold">${match}</span>`
                  );
                }, p.description),
              }}
            />
          </div>
        ),
      }));

      setSuggestions(newSuggestions);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isRtl]
  );

  const onInput = async (val: any, meta: any) => {
    if (meta.action !== 'input-change' || val?.length < 3) {
      return;
    }
    setInputText(val);

    const newIsRtl = val.match(/[\u0590-\u05FF]/);
    if (newIsRtl !== isRtl) setIsRtl(!!newIsRtl);

    performSearch(val);
  };

  const handleSelect = async (placeId: string) => {
    setIsMenuOpen(false);
    prevSuggestions.current = suggestions;

    const { result, status }: any = await getGooglePredictionDetails(placeId);

    if (status === google.maps.places.PlacesServiceStatus.OK && result) {
      const countryIndex =
        result?.address_components?.findIndex((item: any) => item.types.includes('country')) ?? -1;

      const selectedSuggestion = suggestions.find((s) => s.value === placeId);
      const clearresult = {
        address_components: result.address_components,
        formatted_address: result.formatted_address,
        geometry: result.geometry,
        name: result.name,
        types: result.types,
      };

      if (props.sessionStorageKey) {
        sessionStorage.setItem(props.sessionStorageKey, JSON.stringify(clearresult));
      }

      props.onSelect({
        lat: result.geometry?.location?.lat ?? 0,
        lng: result.geometry?.location?.lng ?? 0,
        city: selectedSuggestion?.label ?? '',
        id: countryIndex >= 0 ? result.address_components![countryIndex].short_name : '',
        types: JSON.stringify(result.types ?? []),
        locationName: result.name ?? '',
        fullValue: clearresult,
        placeId: placeId,
      });
    }
  };

  return (
    <div
      className={
        'position-relative' +
        (isRtl ? ' rtl-autocomplete' : ' ltr-autocomplete') +
        (props.isSubmitted && !inputText ? ' is-invalid-location' : '')
      }
    >
      <SelectCustom
        options={suggestions}
        onChange={(e) => handleSelect(e)}
        onInputChange={(e, actionMeta) => onInput(e, actionMeta)}
        value={props.value}
        movablePlaceholder={props.movablePlaceholder}
        movablePlaceholderIcon={faLocationDot}
        filterOption={() => true}
        menuIsOpen={isMenuOpen}
        onBlur={() => {
          setSuggestions(prevSuggestions.current);
          setIsMenuOpen(false);
        }}
        saveInputValue={true}
      />
    </div>
  );
};

export default withErrorBoundary(AutocompleteGoogle, 'AutocompleteGoogle');
